import { cn } from '@utils/cn';
import React from 'react';

interface InvoiceTotalsSummaryProps {
  subtotal: number;
  taxRate: number;
  tax: number;
  discountRate: number;
  discountFixedAmount?: number;
  discountAmount: number;
  total: number;
  currency?: string;
  className?: string;
}

const formatCurrency = (amount: number, currency = 'USD'): string => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

const formatPercent = (value: number): string => `${Number(value).toFixed(2)}%`;

interface RowProps {
  label: string;
  amount: number;
  bold?: boolean;
  currency: string;
  hint?: string;
  negative?: boolean;
}

const Row: React.FC<RowProps> = ({ label, amount, bold, currency, hint, negative }) => (
  <div className="flex items-center justify-between py-1">
    <span className={cn('text-sm text-gray-600', bold && 'font-semibold text-gray-900')}>
      {label}
      {hint && <span className="ml-1 text-xs text-gray-400">({hint})</span>}
    </span>
    <span className={cn('text-sm text-gray-700', bold && 'font-semibold text-gray-900 text-base')}>
      {negative && amount > 0 ? '−' : ''}
      {formatCurrency(amount, currency)}
    </span>
  </div>
);

export const InvoiceTotalsSummary: React.FC<InvoiceTotalsSummaryProps> = ({
  subtotal,
  taxRate,
  tax,
  discountRate,
  discountFixedAmount = 0,
  discountAmount,
  total,
  currency = 'USD',
  className = '',
}) => {
  const showDiscountRow = discountRate > 0 || discountFixedAmount > 0 || discountAmount > 0;
  return (
    <div className={cn('w-full max-w-sm ml-auto border-t border-gray-200 pt-3', className)}>
      <Row label="Subtotal" amount={subtotal} currency={currency} />
      {showDiscountRow && (
        <Row
          label="Discount"
          hint={discountRate > 0 ? formatPercent(discountRate) : undefined}
          amount={discountAmount}
          currency={currency}
          negative
        />
      )}
      {taxRate > 0 && (
        <Row label="Tax" hint={formatPercent(taxRate)} amount={tax} currency={currency} />
      )}
      <div className="border-t border-gray-200 mt-2 pt-2">
        <Row label="Total" amount={total} bold currency={currency} />
      </div>
    </div>
  );
};
